import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import DownloadIcon from '@mui/icons-material/Download';
import LanguageSelectorForPdfDowloadNew from "@/components/popups/invoice.pdf.download.popup";
import InvoiceBindPopup from "@/components/popups/invoice.bind.popup";
import IssueInvoice from "@/components/popups/Issue.invoice.popup";
import InvoiceClone from "@/components/popups/invoice.clone.popup";
import InvoiceCorrectionCreatePopup from "@/components/popups/invoice.correction.create.popup";
import InvoiceCorrectionZeroPopup from "@/components/popups/invoice.correction.zero.popup";
import InvoiceSendKsefPopup from "@/components/popups/invoice.send.ksef.popup";
import { getInvoiceKsefStatus } from "@/service/weexpertService";
import { INVOICE_TYPES, ROLES } from "@/constants";
import { MODE } from "./useInvoiceForm";

export const InvoiceFormActions = ({ form, onSave, isPending = false, isCorrection = false, isCorrectable = false }) => {
  const { t } = useTranslation();

  const {
    mode,
    isViewMode,
    user,
    invoice,
  } = form;

  const invoiceId = invoice?.id;
  const invoiceIdString = invoice?.invoiceId;
  const invoiceType = invoice?.invoiceType;

  // Create mode - just show Save button
  if (mode === MODE.CREATE) {
    return (
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between pt-8 pb-0 md:py-8 items-center flex-wrap">
          <div className="w-full md:w-5/12 text-2xl md:text-4xl pb-2 md:pb-0">
            {t("invoices:create_title")}
          </div>
          <div className=''>
            <Button
              className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
              disabled={isPending}
              onClick={onSave}
            >
              {t("common:button_save")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show Save button
  if (mode === MODE.EDIT) {
    return (
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between pt-8 pb-0 md:py-8 items-center flex-wrap">
          <div className="w-full md:w-5/12 text-2xl md:text-4xl pb-2 md:pb-0">
            {t("invoices:edit_title")}
          </div>
          <div className=''>
            <Button
              className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
              disabled={isPending}
              onClick={onSave}
            >
              {t("common:button_save")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // View mode - show Clone, Issue, Bind, Download, Edit buttons
  if (isViewMode && invoice) {
    const isClient = user?.roles === ROLES.CLIENT;
    const hasFeePurchase = Array.isArray(user?.features) && user.features.includes("FEE_PAYMENT");
    const isAdmin = [ROLES.ADMIN, ROLES.HEAD_OF_DEPARTMENT, ROLES.SUPER_ADMIN].includes(user?.roles);
    const canClone = !isClient || !hasFeePurchase;
    const canIssue = !isClient && invoiceType !== 'SALES_INVOICE' && invoiceType !== 'FAKTURA_KONCOWA' && !isCorrection;
    const canBind = invoiceType === 'FAKTURA_KONCOWA' && !isClient;
    const canEdit = (!isClient || invoiceType === INVOICE_TYPES.PROFORMA) && (!isClient || !hasFeePurchase) && !isCorrection;
    const canCreateCorrection = isCorrectable && isAdmin;
    const canSendToKsef = isAdmin
      && invoiceType !== INVOICE_TYPES.PROFORMA
      && invoice?.state === 'APPROVED'
      && invoice?.ksefStatus !== 'SUBMITTED';

    return (
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="w-full flex justify-between pt-4 md:py-8 pr-8 items-center bg-grayLightMainBg flex-wrap [1147px]:flex-nowrap">
          <div className='w-full md:w-6/12 flex items-baseline flex-wrap md:flex-nowrap justify-between md:justify-start'>
            <div className="ml-8 text-2xl md:text-3xl">
              {t("invoices:details_title")}
            </div>
            <div className='ml-6 [1147px]:ml-0 pt-2 pl-2 text-xl font-bold'>
              {invoiceIdString}
            </div>
          </div>

          <div className='flex pt-2 pl-8 justify-end items-center gap-2'>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none">
                  {t("common:action")} <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-1 flex flex-col">
                {canClone && (
                  <InvoiceClone
                    role={user?.roles}
                    id={invoiceId}
                    invoiceId={invoiceIdString}
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                  />
                )}
                {canIssue && (
                  <IssueInvoice
                    invoiceType={invoiceType}
                    id={invoiceId}
                    invoiceId={invoiceIdString}
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                  />
                )}
                {canBind && (
                  <InvoiceBindPopup
                    invoiceId={invoiceIdString}
                    clientId={invoice.client?.id}
                    id={invoiceId}
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                  />
                )}
                {canCreateCorrection && (
                  <>
                    <InvoiceCorrectionCreatePopup
                      id={invoiceId}
                      invoiceId={invoiceIdString}
                      className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                    />
                    <InvoiceCorrectionZeroPopup
                      id={invoiceId}
                      invoiceId={invoiceIdString}
                      className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                    />
                  </>
                )}
                {canSendToKsef && (
                  <InvoiceSendKsefPopup
                    id={invoiceId}
                    invoiceId={invoiceIdString}
                    icon={null}
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                  />
                )}
                {isAdmin && (
                  <Button
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                    onClick={() => getInvoiceKsefStatus(invoiceId).then(res => console.log('KSeF Status:', res.data))}
                  >
                    KSeF Status
                  </Button>
                )}
                <LanguageSelectorForPdfDowloadNew
                    className="w-full justify-start bg-transparent hover:bg-gray-100 border-none shadow-none mr-0 rounded-sm text-blueText opacity-70"
                    icon={<DownloadIcon fontSize="small" />}
                    selectedItems={[invoiceId]}
                  />
              </PopoverContent>
            </Popover>
            {canEdit && (
              <Link style={{ textDecoration: 'none' }} to={`/admin/invoices/details/edit/${invoiceId}`}>
                <Button className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none">
                  {t("common:button_change")}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
